class AssemblerElementDynamic extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.selectedMainProductId = null;
    this.selectedMainVariantId = null;
    this.selectedComplementaryProductId = null;
    this.selectedComplementaryVariantId = null;
    this.status = 'selecting_main_product';
    this.mainSlider = null;
    this.slider = null;
    this.mainCurrentIndex = 0;
    this.compCurrentIndex = 0;
  }

  connectedCallback() {
    this.setup();
    
    // Listen for color selection events
    document.addEventListener('metaobject-color-selected', (event) => {
      const { collectionHandle, collectionTitle } = event.detail;
      if (collectionHandle && collectionTitle) {
        this.loadCollectionProducts(collectionHandle, collectionTitle);
      }
    });
  }

  setup() {
    const configScript = this.querySelector('script[data-products-config]');
    if (configScript) {
      this.config = JSON.parse(configScript.textContent);
    }

    this.getAllElements();
    this.renderProductSlides();

    // Defer Swiper initialization to the next frame so the browser has time to
    // lay out the dynamically injected slides before Swiper measures their widths.
    this._initRaf = requestAnimationFrame(() => {
      this._initRaf = null;
      this.initSwiper();
      this.setEventListeners();
    });
  }

  getAllElements() {
    this.mainProductImage = this.querySelector('.assemble-your-set__main-product-image');
    this.mainProductBadge = this.querySelector('.assemble-your-set__main-product-badge');
    this.mainProductForm = this.querySelector('.assemble-your-set__main-product-form');
    this.mainProductData = this.querySelector('.assemble-your-set__main-product-data');
    this.mainProductTitle = this.mainProductData?.querySelector('.assemble-your-set__product-title');
    this.mainProductPrice = this.mainProductData?.querySelector('.assemble-your-set__product-price');

    // Main product carousel elements
    this.mainSliderHolder = this.querySelector('.assemble-your-set__main-slider-holder');
    this.mainNavPrev = this.querySelector('[data-main-nav-prev]');
    this.mainNavNext = this.querySelector('[data-main-nav-next]');
    this.mainCurrentSlideEl = this.querySelector('[data-main-current-slide]');
    this.mainVariantsContainer = this.querySelector('[data-main-variants]');

    this.complementaryData = this.querySelector('.assemble-your-set__complementary-products-data');
    this.complementaryForm = this.querySelector('.assemble-your-set__complementary-products-form');
    this.complementaryTitle = this.querySelector('[data-comp-title]');
    this.complementaryPrice = this.querySelector('[data-comp-price]');
    this.complementaryVariantsContainer = this.querySelector('[data-comp-variants]');

    this.sliderHolder = this.querySelector('.assemble-your-set__slider-holder');
    this.navPrev = this.querySelector('[data-nav-prev]');
    this.navNext = this.querySelector('[data-nav-next]');
    this.currentSlideEl = this.querySelector('[data-current-slide]');
    this.sectionMessage = this.querySelector('.assemble-your-set__section-message');
    this.alreadyInCartMessage = this.querySelector('.assemble-your-set__section-already-in-cart-message');
    this.setInCartMessage = this.querySelector('.assemble-your-set__section-set-in-cart-message');

    this.submitButton = this.querySelector('[data-submit-assemble]');
    this.removeButton = this.querySelector('[data-remove-from-cart]');
    this.removeSetButton = this.querySelector('[data-remove-set-from-cart]');
  }

  initSwiper() {
    if (typeof BlazeSlider === 'undefined') {
      console.warn('BlazeSlider not loaded');
      return;
    }

    const blazeConfig = {
      all: {
        slidesToShow: 1,
        slidesToScroll: 1,
        loop: false,
        draggable: true,
        enablePagination: false,
        transitionDuration: 300,
      }
    };

    // Initialize main product carousel if enabled
    if (this.config.enableMainCarousel && this.mainSliderHolder && this.config.mainProducts.length > 0) {
      const mainEl = this.querySelector('.assemble-your-set__main-slider-container');
      if (mainEl) {
        this.mainCurrentIndex = 0;
        this.mainSlider = new BlazeSlider(mainEl, blazeConfig);
        this.mainSlider.onSlide((pageIndex, firstSlideIndex) => {
          this.mainCurrentIndex = firstSlideIndex;
          this.handleMainSlideChange(firstSlideIndex);
        });
      }
      this.showInitialMainProduct();
    } else if (this.config.mainProducts.length > 0) {
      // Single product mode — still show the product data
      this.showInitialMainProduct();
    }

    // Initialize complementary products carousel
    if (this.config.complementaryProducts.length > 0) {
      const compEl = this.querySelector('.assemble-your-set__slider-container');
      if (compEl) {
        this.compCurrentIndex = 0;
        this.slider = new BlazeSlider(compEl, blazeConfig);
        this.slider.onSlide((pageIndex, firstSlideIndex) => {
          this.compCurrentIndex = firstSlideIndex;
          this.handleSlideChange(firstSlideIndex);
        });
      }
    }

    this.showInitialComplementaryProduct();
    this.updateComplementaryBlockState('selecting_main_product');
  }

  showInitialComplementaryProduct() {
    if (this.config.complementaryProducts.length > 0) {
      const firstProduct = this.config.complementaryProducts[0];
      this.selectedComplementaryProductId = firstProduct.id;
      this.renderComplementaryProduct(firstProduct);

      // Mark first slide as active
      const firstSlide = this.querySelector('.assemble-your-set__slider-container .blaze-track > li');
      if (firstSlide) {
        firstSlide.classList.add('is-active');
      }

      // Update total slides counter
      const totalSlidesEl = this.querySelector('[data-total-slides]');
      if (totalSlidesEl) {
        totalSlidesEl.textContent = this.config.complementaryProducts.length;
      }

      // Update nav buttons initial state
      this.updateNavButtons();
    }
  }

  showInitialMainProduct() {
    if (this.config.mainProducts.length > 0) {
      const firstProduct = this.config.mainProducts[0];
      this.selectedMainProductId = firstProduct.id;
      this.renderMainProduct(firstProduct);

      // Mark first slide as active
      const firstSlide = this.querySelector('.assemble-your-set__main-slider-container .blaze-track > li');
      if (firstSlide) {
        firstSlide.classList.add('is-active');
      }

      // Update main total slides counter
      const mainTotalSlidesEl = this.querySelector('[data-main-total-slides]');
      if (mainTotalSlidesEl) {
        mainTotalSlidesEl.textContent = this.config.mainProducts.length;
      }

      // Update nav buttons initial state
      this.updateMainNavButtons();
    }
  }

  handleMainSlideChange(activeIndex = 0) {
    const product = this.config.mainProducts[activeIndex];
    if (!product) return;

    if (this.mainCurrentSlideEl) {
      this.mainCurrentSlideEl.textContent = activeIndex + 1;
    }
    this.querySelectorAll('.assemble-your-set__main-slider-container .blaze-track > li').forEach((s, i) => {
      s.classList.toggle('is-active', i === activeIndex);
      s.querySelector('.assemble-your-set__main-product-badge')?.classList.add('d-hidden');
    });
    this.selectedMainProductId = product.id;
    this.selectedMainVariantId = null;
    this.renderMainProduct(product);
    this.changeStatus('selecting_main_product');
    this.updateMainNavButtons();
  }

  renderMainProduct(product) {
    // Update title and price
    if (this.mainProductTitle) {
      this.mainProductTitle.textContent = product.title;
    } else {
      console.warn('⚠️ [Dynamic] mainProductTitle element NOT found');
    }
    if (this.mainProductPrice) {
      this.mainProductPrice.textContent = this.formatPrice(product.price);
    } else {
      console.warn('⚠️ [Dynamic] mainProductPrice element NOT found');
    }

    // Render size variants
    const uniqueSizes = this.getUniqueSizes(product.variants);
    const allUnavailable = uniqueSizes.every(v => !v.available);
    const variantsHtml = uniqueSizes.map(variant => `
      <input
        data-variant-id="${variant.id}"
        id="main-size-${product.handle}-${variant.size}-input"
        type="radio"
        name="main-size"
        value="${variant.size}"
        ${!variant.available ? 'disabled data-unavailable' : ''}
      >
      <label
        data-value="${variant.size}"
        for="main-size-${product.handle}-${variant.size}-input"
        title="${variant.size}"
        class="assemble-your-set__option-label product-option__label product-option__label--rounded product-option__label--circle"
        ${!variant.available ? 'disabled' : ''}
      >
        <span>${variant.size}</span>
        ${!variant.available ? '<span class="visually-hidden label-unavailable">Variante esgotada ou indisponível</span>' : ''}
      </label>
    `).join('');

    // Update variants in form
    const variantsContainer = this.mainProductForm?.querySelector('.assemble-your-set__product-variants');
    if (variantsContainer) {
      variantsContainer.innerHTML = allUnavailable
        ? `<p class="assemble-your-set__sold-out-message d-m-0" style="color:#c00;font-weight:600;">Produto esgotado — selecione outra peça no carrossel</p>`
        : variantsHtml;
    }
  }

  updateMainNavButtons() {
    if (this.mainNavPrev) {
      this.mainNavPrev.disabled = this.mainCurrentIndex === 0;
    }
    if (this.mainNavNext) {
      this.mainNavNext.disabled = this.mainCurrentIndex >= this.config.mainProducts.length - 1;
    }
  }

  handleSlideChange(activeIndex = 0) {
    const product = this.config.complementaryProducts[activeIndex];
    if (!product) return;

    if (this.currentSlideEl) {
      this.currentSlideEl.textContent = activeIndex + 1;
    }
    this.querySelectorAll('.assemble-your-set__slider-container .blaze-track > li').forEach((s, i) => {
      s.classList.toggle('is-active', i === activeIndex);
      s.querySelector('.assemble-your-set__slide-badge')?.classList.add('d-hidden');
    });
    this.selectedComplementaryProductId = product.id;
    this.selectedComplementaryVariantId = null;
    this.renderComplementaryProduct(product);
    if (this.selectedMainVariantId) {
      this.changeStatus('selecting_complementary_product');
    }
    this.updateNavButtons();
  }

  updateNavButtons() {
    if (this.navPrev) {
      this.navPrev.disabled = this.compCurrentIndex === 0;
    }
    if (this.navNext) {
      this.navNext.disabled = this.compCurrentIndex >= this.config.complementaryProducts.length - 1;
    }
  }

  setEventListeners() {
    // Remove previous listeners to avoid duplicates on reinit
    if (this._boundHandlers) {
      this.mainProductForm?.removeEventListener('change', this._boundHandlers.mainFormChange);
      this.complementaryForm?.removeEventListener('change', this._boundHandlers.compFormChange);
      this.submitButton?.removeEventListener('click', this._boundHandlers.submit);
      this.removeButton?.removeEventListener('click', this._boundHandlers.remove);
      this.removeSetButton?.removeEventListener('click', this._boundHandlers.removeSet);
      this.navPrev?.removeEventListener('click', this._boundHandlers.navPrev);
      this.navNext?.removeEventListener('click', this._boundHandlers.navNext);
      this.mainNavPrev?.removeEventListener('click', this._boundHandlers.mainNavPrev);
      this.mainNavNext?.removeEventListener('click', this._boundHandlers.mainNavNext);
    }
    
    this._boundHandlers = {
      mainFormChange: (e) => { if (e.target.type === 'radio') this.handleMainSizeChange(e.target); },
      compFormChange: (e) => { if (e.target.type === 'radio') this.handleComplementarySizeChange(e.target); },
      submit: () => this.handleSubmit(),
      remove: () => this.handleRemove(),
      removeSet: () => this.handleRemoveSet(),
      navPrev: () => this.slider?.prev(),
      navNext: () => this.slider?.next(),
      mainNavPrev: () => this.mainSlider?.prev(),
      mainNavNext: () => this.mainSlider?.next(),
    };
    
    this.mainProductForm?.addEventListener('change', this._boundHandlers.mainFormChange);
    this.complementaryForm?.addEventListener('change', this._boundHandlers.compFormChange);
    this.submitButton?.addEventListener('click', this._boundHandlers.submit);
    this.removeButton?.addEventListener('click', this._boundHandlers.remove);
    this.removeSetButton?.addEventListener('click', this._boundHandlers.removeSet);
    this.navPrev?.addEventListener('click', this._boundHandlers.navPrev);
    this.navNext?.addEventListener('click', this._boundHandlers.navNext);
    this.mainNavPrev?.addEventListener('click', this._boundHandlers.mainNavPrev);
    this.mainNavNext?.addEventListener('click', this._boundHandlers.mainNavNext);
  }

  handleMainSizeChange(input) {
    const variantId = parseInt(input.dataset.variantId);
    this.selectedMainVariantId = variantId;

    // Find the current main product (from carousel or single)
    const currentMainProduct = this.config.mainProducts.find(p => p.id === this.selectedMainProductId) || this.config.mainProducts[0];
    
    // Update image if variant has specific image
    const variant = currentMainProduct?.variants.find(v => v.id === variantId);

    // Update price to match selected variant
    if (variant && this.mainProductPrice) {
      this.mainProductPrice.textContent = this.formatPrice(variant.price);
    }
    
    if (this.config.enableMainCarousel) {
      // Carousel mode: update only the active slide's image
      const activeSlide = this.querySelector('.assemble-your-set__main-slider-container .blaze-track > li.is-active');
      if (activeSlide) {
        if (variant?.image) {
          const slideImage = activeSlide.querySelector('.assemble-your-set__main-product-image');
          if (slideImage) {
            slideImage.src = variant.image;
          }
        }
        // Show badge on active slide
        activeSlide.querySelector('.assemble-your-set__main-product-badge')?.classList.remove('d-hidden');
      }
    } else {
      // Single product mode: update the main product image
      if (variant?.image && this.mainProductImage) {
        this.mainProductImage.src = variant.image;
      }
    }

    this.changeStatus('main_product_selected');
  }

  renderComplementaryProduct(product) {
    // Update title and price
    if (this.complementaryTitle) {
      this.complementaryTitle.textContent = product.title;
    } else {
      console.warn('⚠️ [Dynamic] complementaryTitle element NOT found');
    }
    if (this.complementaryPrice) {
      this.complementaryPrice.textContent = this.formatPrice(product.price);
    } else {
      console.warn('⚠️ [Dynamic] complementaryPrice element NOT found');
    }

    // Render size variants with unique IDs to avoid conflicts
    const productHandle = product.handle || product.id;
    const uniqueSizes = this.getUniqueSizes(product.variants);
    const allUnavailable = uniqueSizes.every(v => !v.available);
    const variantsHtml = uniqueSizes.map(variant => `
      <input
        data-variant-id="${variant.id}"
        id="comp-size-${productHandle}-${variant.size}-input"
        type="radio"
        name="complementary-size"
        value="${variant.size}"
        ${!variant.available ? 'disabled data-unavailable' : ''}
      >
      <label
        data-value="${variant.size}"
        for="comp-size-${productHandle}-${variant.size}-input"
        title="${variant.size}"
        class="assemble-your-set__option-label product-option__label product-option__label--rounded product-option__label--circle"
        ${!variant.available ? 'disabled' : ''}
      >
        <span>${variant.size}</span>
        ${!variant.available ? '<span class="visually-hidden label-unavailable">Variante esgotada ou indisponível</span>' : ''}
      </label>
    `).join('');

    if (this.complementaryVariantsContainer) {
      this.complementaryVariantsContainer.innerHTML = allUnavailable
        ? `<p class="assemble-your-set__sold-out-message d-m-0" style="color:#c00;font-weight:600;">Produto esgotado — selecione outra peça no carrossel</p>`
        : variantsHtml;
    }
    
    // Clear complementary selection if all variants are unavailable
    if (allUnavailable) {
      this.selectedComplementaryVariantId = null;
      this.updateButtonState();
    }

    // Show complementary form
    this.complementaryData?.classList.remove('d-hidden');
    this.complementaryForm?.classList.remove('d-hidden');
  }

  getUniqueSizes(variants) {
    const seen = new Set();
    return variants.filter(v => {
      if (seen.has(v.size)) return false;
      seen.add(v.size);
      return true;
    });
  }

  handleComplementarySizeChange(input) {
    const variantId = parseInt(input.dataset.variantId);
    this.selectedComplementaryVariantId = variantId;

    // Update price to match selected variant
    const currentCompProduct = this.config.complementaryProducts.find(p => p.id === this.selectedComplementaryProductId) || this.config.complementaryProducts[this.compCurrentIndex];
    const variant = currentCompProduct?.variants.find(v => v.id === variantId);
    if (variant && this.complementaryPrice) {
      this.complementaryPrice.textContent = this.formatPrice(variant.price);
    }

    // Show badge on selected complementary slide (use specific selector)
    const activeSlide = this.querySelector('.assemble-your-set__slider-container .blaze-track > li.is-active');
    if (activeSlide) {
      activeSlide.querySelector('.assemble-your-set__slide-badge')?.classList.remove('d-hidden');
    }

    this.changeStatus('complementary_product_selected');
  }

  changeStatus(newStatus) {
    this.status = newStatus;
    this.dataset.status = newStatus;

    // Update message
    const message = this.config.statusDictionary.section_message[newStatus];
    if (this.sectionMessage && message) {
      const stepNumber = this.getStepNumber(newStatus);
      this.sectionMessage.innerHTML = `<strong>Passo ${stepNumber}: </strong>${message}`;
    }

    // Update button text
    const buttonText = this.config.statusDictionary.button_text[newStatus];
    if (this.submitButton && buttonText) {
      this.submitButton.textContent = buttonText;
    }

    // Show/hide main product badge
    if (newStatus !== 'selecting_main_product') {
      this.mainProductBadge?.classList.remove('d-hidden');
    } else {
      this.mainProductBadge?.classList.add('d-hidden');
    }

    // Block/unblock complementary section based on main product selection (carousel mode)
    this.updateComplementaryBlockState(newStatus);

    // Slider is always enabled now
    this.sliderHolder?.classList.remove('is-disabled');

    // Disable button based on status
    this.updateButtonState();
  }

  updateComplementaryBlockState(status) {
    const isBlocked = status === 'selecting_main_product';
    
    // Toggle blocked class on complementary elements (but NOT the slider holder - keep carousel always free)
    this.complementaryData?.classList.toggle('is-blocked', isBlocked);
    this.complementaryForm?.classList.toggle('is-blocked', isBlocked);

    // Disable/enable complementary variant inputs
    const complementaryInputs = this.complementaryVariantsContainer?.querySelectorAll('input[type="radio"]');
    complementaryInputs?.forEach(input => {
      input.disabled = isBlocked || input.hasAttribute('data-unavailable');
    });
  }

  getStepNumber(status) {
    switch (status) {
      case 'selecting_main_product':
        return 1;
      case 'main_product_selected':
      case 'selecting_complementary_product':
        return 2;
      case 'complementary_product_selected':
      case 'finished':
        return 3;
      default:
        return 1;
    }
  }

  updateButtonState() {
    if (!this.submitButton) return;

    // Button is only enabled when both products have selected variants
    const bothSelected = this.selectedMainVariantId && this.selectedComplementaryVariantId;
    this.submitButton.disabled = !bothSelected || this.status === 'finished';
  }

  async handleSubmit() {
    // Only proceed if both variants are selected
    if (!this.selectedMainVariantId || !this.selectedComplementaryVariantId) {
      console.warn('⚠️ [Dynamic] handleSubmit aborted — missing variant IDs');
      return;
    }

    // Disable button during submission
    this.submitButton.disabled = true;
    
    // Add both products at once
    const result = await this.addBothToCart();

    if (result?.error) {
      // Show error to user and re-enable button
      this.showCartError(result.message);
      this.submitButton.disabled = false;
      return;
    }
    
    this.changeStatus('finished');
    await this.refreshCartAndOpen();
  }
  
  showCartError(message) {
    // Show error inline in the section message
    if (this.sectionMessage) {
      this.sectionMessage.innerHTML = `<strong style="color: #c00;">${message}</strong>`;
    }
    // Auto-clear after 5s
    setTimeout(() => {
      if (this.status !== 'finished') {
        this.changeStatus(this.status);
      }
    }, 5000);
  }

  async addBothToCart() {
    try {
      const items = [
        { id: this.selectedMainVariantId, quantity: 1, properties: { '_origem': 'Monte seu Conjunto - Página' } },
        { id: this.selectedComplementaryVariantId, quantity: 1, properties: { '_origem': 'Monte seu Conjunto - Página' } }
      ];
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('❌ [Dynamic] Cart API error:', responseData);
        return { error: true, message: responseData.description || responseData.message || 'Erro ao adicionar ao carrinho' };
      }
      return responseData;
    } catch (error) {
      console.error('❌ [Dynamic] Error adding to cart:', error);
      return { error: true, message: 'Erro de conexão ao adicionar ao carrinho' };
    }
  }

  async addToCart(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { '_origem': 'Monte seu Conjunto - Página' } }]
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      // Trigger cart update event
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'assemble-your-set' });
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async handleRemove() {
    if (this.selectedMainVariantId) {
      await this.removeFromCart(this.selectedMainVariantId);
      this.resetToStart();
    }
  }

  async handleRemoveSet() {
    const promises = [];
    if (this.selectedMainVariantId) {
      promises.push(this.removeFromCart(this.selectedMainVariantId));
    }
    if (this.selectedComplementaryVariantId) {
      promises.push(this.removeFromCart(this.selectedComplementaryVariantId));
    }
    await Promise.all(promises);
    this.resetToStart();
  }

  async removeFromCart(variantId) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      });

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'assemble-your-set' });
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  resetToStart() {
    this.selectedMainVariantId = null;
    this.selectedComplementaryProductId = null;
    this.selectedComplementaryVariantId = null;

    // Reset forms
    this.mainProductForm?.reset();
    this.complementaryForm?.reset();

    // Hide complementary section
    this.complementaryData?.classList.add('d-hidden');
    this.complementaryForm?.classList.add('d-hidden');

    // Remove active states from complementary slides
    this.querySelectorAll('.assemble-your-set__slider-container .blaze-track > li').forEach(s => {
      s.classList.remove('is-active');
      s.querySelector('.assemble-your-set__slide-badge')?.classList.add('d-hidden');
    });

    this.changeStatus('selecting_main_product');
  }

  async refreshCartAndOpen() {
    try {
      // Fetch updated cart to trigger Shopify's cart update
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Dispatch cart update event for theme integration
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      // Try Dawn's cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Dawn uses a fetch to update cart contents
        const drawerResponse = await fetch('/?section_id=cart-drawer');
        const html = await drawerResponse.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        
        if (newDrawer) {
          cartDrawer.innerHTML = newDrawer.innerHTML;
        }
        
        // Open the drawer
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('is-open', 'active');
          document.body.classList.add('overflow-hidden');
        }
      }

      // Fallback: update cart icon count
      const cartCount = document.querySelector('.cart-count-bubble span');
      if (cartCount && cart.item_count) {
        cartCount.textContent = cart.item_count;
      }
    } catch (e) {
      console.warn('Cart update error:', e);
      // Fallback: redirect to cart page
      window.location.href = '/cart';
    }
  }

  formatPrice(cents) {
    const price = (cents / 100).toFixed(2).replace('.', ',');
    return `R$ ${price}`;
  }

  async loadCollectionProducts(collectionHandle, collectionTitle) {
    // Deduplicate: ignore if already loading this exact collection
    if (this._loadingHandle === collectionHandle) {
      return;
    }
    this._loadingHandle = collectionHandle;

    try {
      // Show loading overlay
      this.classList.add('is-loading');
      
      // Fetch collection metafields using Storefront API
      const query = `
        query getCollectionProducts($handle: String!) {
          collection(handle: $handle) {
            handle
            title
            metafield1: metafield(namespace: "custom", key: "monte_seu_conjunto_topo") {
              type
              references(first: 10) {
                edges {
                  node {
                    ... on Product {
                      id
                      handle
                      title
                      featuredImage {
                        url(transform: {maxWidth: 400})
                      }
                      fotoStill: metafield(namespace: "custom", key: "foto_still_monte_seu_conjunto") {
                        reference {
                          ... on MediaImage {
                            image {
                              url(transform: {maxWidth: 400})
                            }
                          }
                        }
                      }
                      priceRange {
                        minVariantPrice {
                          amount
                        }
                      }
                      variants(first: 50) {
                        edges {
                          node {
                            id
                            title
                            availableForSale
                            quantityAvailable
                            currentlyNotInStock
                            price {
                              amount
                            }
                            selectedOptions {
                              name
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            metafield2: metafield(namespace: "custom", key: "monte_seu_conjunto_parte_de_baixo") {
              type
              references(first: 10) {
                edges {
                  node {
                    ... on Product {
                      id
                      handle
                      title
                      featuredImage {
                        url(transform: {maxWidth: 400})
                      }
                      fotoStill: metafield(namespace: "custom", key: "foto_still_monte_seu_conjunto") {
                        reference {
                          ... on MediaImage {
                            image {
                              url(transform: {maxWidth: 400})
                            }
                          }
                        }
                      }
                      priceRange {
                        minVariantPrice {
                          amount
                        }
                      }
                      variants(first: 50) {
                        edges {
                          node {
                            id
                            title
                            availableForSale
                            quantityAvailable
                            currentlyNotInStock
                            price {
                              amount
                            }
                            selectedOptions {
                              name
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      // Get Storefront API token from theme settings
      const storefrontToken = window.themeSettings?.storefront_api_token || '';
      
      if (!storefrontToken) {
        console.error('❌ [AssembleYourSet] Storefront API token not found');
        this.classList.remove('is-loading');
        return;
      }
      
      const response = await fetch(`https://${window.Shopify.shop}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken
        },
        body: JSON.stringify({
          query,
          variables: { handle: collectionHandle }
        })
      });
      
      const result = await response.json();

      if (result.errors) {
        console.error('❌ [AssembleYourSet] GraphQL errors:', result.errors);
        this.classList.remove('is-loading');
        return;
      }
      
      const collection = result.data?.collection;
      if (!collection) {
        console.error('❌ [AssembleYourSet] Collection not found');
        this.classList.remove('is-loading');
        return;
      }
      
      // Transform GraphQL response to config format
      const mainProducts = collection.metafield1?.references?.edges?.map(edge => {
        const product = edge.node;
        const productId = product.id.split('/').pop();
        const stillImage = product.fotoStill?.reference?.image?.url;
        const productImage = stillImage || product.featuredImage?.url || '';
        
        return {
          id: parseInt(productId),
          handle: product.handle,
          title: product.title,
          image: productImage,
          price: parseFloat(product.priceRange.minVariantPrice.amount) * 100,
          variants: product.variants.edges.map(vEdge => {
            const variant = vEdge.node;
            const variantId = variant.id.split('/').pop();
            const sizeOption = variant.selectedOptions.find(opt => 
              opt.name.toLowerCase() === 'tamanho' || opt.name.toLowerCase() === 'size'
            );
            // Variant is truly available only if availableForSale AND not currently out of stock
            // currentlyNotInStock = true means the item is sold out but "continue selling" is on
            const qty = variant.quantityAvailable;
            const isAvailable = variant.availableForSale && !variant.currentlyNotInStock && (qty === null || qty === undefined || qty > 0);
            
            return {
              id: parseInt(variantId),
              title: variant.title,
              size: sizeOption?.value || variant.title,
              available: isAvailable,
              price: parseFloat(variant.price.amount) * 100,
              image: productImage
            };
          })
        };
      }) || [];
      
      const complementaryProducts = collection.metafield2?.references?.edges?.map(edge => {
        const product = edge.node;
        const productId = product.id.split('/').pop();
        const stillImage = product.fotoStill?.reference?.image?.url;
        const productImage = stillImage || product.featuredImage?.url || '';
        
        return {
          id: parseInt(productId),
          handle: product.handle,
          title: product.title,
          image: productImage,
          price: parseFloat(product.priceRange.minVariantPrice.amount) * 100,
          variants: product.variants.edges.map(vEdge => {
            const variant = vEdge.node;
            const variantId = variant.id.split('/').pop();
            const sizeOption = variant.selectedOptions.find(opt => 
              opt.name.toLowerCase() === 'tamanho' || opt.name.toLowerCase() === 'size'
            );
            const qty = variant.quantityAvailable;
            const isAvailable = variant.availableForSale && !variant.currentlyNotInStock && (qty === null || qty === undefined || qty > 0);
            
            return {
              id: parseInt(variantId),
              title: variant.title,
              size: sizeOption?.value || variant.title,
              available: isAvailable,
              price: parseFloat(variant.price.amount) * 100,
              image: productImage
            };
          })
        };
      }) || [];
      
      // Update config
      this.config.mainProducts = mainProducts;
      this.config.complementaryProducts = complementaryProducts;
      this.config.enableMainCarousel = mainProducts.length > 1;
      
      // Reset state
      this.selectedMainProductId = null;
      this.selectedMainVariantId = null;
      this.selectedComplementaryProductId = null;
      this.selectedComplementaryVariantId = null;
      this.status = 'selecting_main_product';
      this.dataset.status = 'selecting_main_product';
      
      // Re-render UI with new products
      this.renderProductSlides();
      
      // Destroy existing sliders
      if (this.mainSlider) {
        this.mainSlider.destroy();
        this.mainSlider = null;
      }
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
      this.mainCurrentIndex = 0;
      this.compCurrentIndex = 0;

      // Cancel any pending rAF from a previous loadCollectionProducts call
      if (this._initRaf) {
        cancelAnimationFrame(this._initRaf);
        this._initRaf = null;
      }

      // Reinitialize — defer to next frame so browser lays out new slides first
      this.getAllElements();
      
      // Reset counters before reinit to prevent stale values
      if (this.mainCurrentSlideEl) {
        this.mainCurrentSlideEl.textContent = '1';
      }
      if (this.currentSlideEl) {
        this.currentSlideEl.textContent = '1';
      }
      const mainTotalEl = this.querySelector('[data-main-total-slides]');
      if (mainTotalEl) {
        mainTotalEl.textContent = this.config.mainProducts?.length || 0;
      }
      const compTotalEl = this.querySelector('[data-total-slides]');
      if (compTotalEl) {
        compTotalEl.textContent = this.config.complementaryProducts?.length || 0;
      }
      
      this._initRaf = requestAnimationFrame(() => {
        this._initRaf = null;
        this._loadingHandle = null;
        this.initSwiper();
        this.setEventListeners();

        // Populate data for first products
        if (this.config.mainProducts?.length > 0) {
          this.handleMainSlideChange(0);
        }
        if (this.config.complementaryProducts?.length > 0) {
          this.handleSlideChange(0);
        }

        this.classList.remove('is-loading');
        const section = this.closest('section');
        section?.classList.remove('d-hidden');
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

    } catch (error) {
      console.error('❌ [AssembleYourSet] Error loading collection products:', error);
      this._loadingHandle = null;
      this.classList.remove('is-loading');
    }
  }

  renderProductSlides() {
    // Render main products
    const mainWrapper = this.querySelector('.assemble-your-set__main-slider-container .blaze-track');
    if (mainWrapper && this.config.mainProducts) {
      mainWrapper.innerHTML = this.config.mainProducts.map((product, idx) => `
        <li
          class="swiper-slide assemble-your-set__main-slide d-flex d-items-center d-justify-center d-w-full d-relative"
          data-main-product-id="${product.id}"
          data-handle="${product.handle}"
        >
          <img
            loading="${idx === 0 ? 'eager' : 'lazy'}"
            width="200"
            height="200"
            class="assemble-your-set__main-product-image d-w-full d-h-auto d-object-cover"
            src="${product.image}"
            alt="${product.title}"
          >
          <p class="assemble-your-set__main-product-badge d-hidden d-absolute" disco-cloak>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 11 8" fill="none">
              <path d="M1.60156 4.27091L3.90556 6.57491L9.04956 1.43091" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </p>
        </li>
      `).join('');
    }
    
    // Render complementary products
    const compWrapper = this.querySelector('.assemble-your-set__slider-container .blaze-track');
    if (compWrapper && this.config.complementaryProducts) {
      compWrapper.innerHTML = this.config.complementaryProducts.map((product, idx) => `
        <li
          class="swiper-slide d-flex d-items-center d-justify-center d-w-full d-relative"
          data-product-id="${product.id}"
          data-handle="${product.handle}"
        >
          <img
            loading="${idx === 0 ? 'eager' : 'lazy'}"
            width="200"
            height="200"
            class="d-w-full d-h-auto d-object-cover"
            src="${product.image}"
            alt="${product.title}"
          >
          <p class="assemble-your-set__slide-badge d-hidden d-absolute" disco-cloak>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 11 8" fill="none">
              <path d="M1.60156 4.27091L3.90556 6.57491L9.04956 1.43091" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </p>
        </li>
      `).join('');
    }
  }
}

if (!customElements.get('assembler-element-dynamic')) {
  customElements.define('assembler-element-dynamic', AssemblerElementDynamic);
}
